//#region Imports

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TrackCategoryProxy } from '../../models/proxies/track-category.proxy';
import { TrackProxy } from '../../models/proxies/track.proxy';
import { AsyncResult } from '../../modules/http-async/models/async-result';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';
import { RatingPayload } from '../../models/payloads/rating.payload';
import { GetManyTrackRatingsProxy } from '../../models/proxies/get-many-track-ratings.proxy';
import { getPaginatedCrudData } from '../../utils/http';
import { GetManyTracks } from '../../models/proxies/get-many-tracks.proxy';

//#endregion

@Injectable({
  providedIn: 'root',
})
export class TrackInteractor {

  //#region Constructors

  constructor(
    private readonly http: HttpAsyncService,
  ) { }

  //#endregion

  //#region Functions

  public async getAllTracks(
    categoryId: string | null, searchText: string, currentPage: number, limit: number
  ): Promise<AsyncResult<GetManyTracks>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      ...categoryId && { categoryId },
      ...searchText && {
        $or: [
          { name: { $contL: searchText } },
        ],
      },
      'courses.isActive': true,
    }));

    const url = environment.api.track.get
      .replace('{searchParams}', searchParams)
      .replace('{page}', String(currentPage))
      .replace('{limit}', String(limit))
      + '&join=trackCourses.course'

    return await this.http.get<GetManyTracks>(url);
  }

  public async getOneTrack(id: string): Promise<AsyncResult<TrackProxy>> {
    return await this.http.get<TrackProxy>(`${environment.routes.track}/${id}?join=trackCourses.course`);
  }

  /**
   * Busca relações track-course por courseId (fallback quando a lista de trilhas vem vazia).
   * Retorna array com { trackId, courseId, course? }.
   */
  public async getTrackCoursesByCourseId(courseId: string): Promise<AsyncResult<{ data: Array<{ trackId: string; courseId: string; course?: unknown }> }>> {
    const searchParams = encodeURIComponent(JSON.stringify({ courseId }));
    const url = environment.api.track.coursesByCourse.replace('{searchParams}', searchParams);
    return await this.http.get<{ data: Array<{ trackId: string; courseId: string; course?: unknown }> }>(url);
  }

  public async getRatingsFromTrack(trackId: string): Promise<AsyncResult<GetManyTrackRatingsProxy>> {
    const searchParams = encodeURIComponent(JSON.stringify({ trackId }));

    const url: string = environment.api.track.ratingsFromTrack.replace('{searchParams}', searchParams) 
                        + '&join=user';

    return await this.http.get<GetManyTrackRatingsProxy>(url);
  }

  public async sendRating(payload: RatingPayload): Promise<AsyncResult<void>> {
    return await this.http.post<void>(environment.api.track.rating, payload);
  }

  public async editRating(payload: RatingPayload): Promise<AsyncResult<void>> {
    return await this.http.put<void>(environment.api.track.rating.replace('{id}',payload.id), payload);
  }

  //#endregion

  async getTrackCategories(): Promise<AsyncResult<TrackCategoryProxy[]>> {
    return await getPaginatedCrudData<TrackCategoryProxy>(this.http, environment.api.track.category.listPaginated);
  }
}
